import React, { useState, useMemo } from 'react';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  IconButton, 
  Paper,
  Chip,
  Avatar,
  Tooltip,
  Collapse,
  Divider,
  alpha,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Intentar registrar ImageResize si está disponible
try {
  const ImageResize = require('quill-image-resize-module-react').default;
  ReactQuill.Quill.register('modules/imageResize', ImageResize);
} catch (e) {
  console.log('ImageResize module not available');
}

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436',
  light: '#dfe6e9'
};

// Paleta de colores extendida para el editor
const TEXT_COLORS = [
  // Neutros
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
  // Rojos
  '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
  // Tonos pastel
  '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
  // Tonos medios
  '#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd',
  // Tonos oscuros
  '#cc4125', '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6d9eeb', '#6fa8dc', '#8e7cc3', '#c27ba0',
  // Colores de la app
  '#00b894', '#0984e3', '#6c5ce7', '#fdcb6e', '#e17055', '#d63031', '#74b9ff', '#55efc4', '#a29bfe', '#fd79a8'
];

const BACKGROUND_COLORS = [
  // Transparente y blancos
  'transparent', '#ffffff', '#f8f9fa', '#f1f3f4', '#e9ecef', '#dee2e6',
  // Colores claros
  '#ffebee', '#fce4ec', '#f3e5f5', '#ede7f6', '#e8eaf6', '#e3f2fd',
  '#e1f5fe', '#e0f7fa', '#e0f2f1', '#e8f5e9', '#f1f8e9', '#f9fbe7',
  '#fffde7', '#fff8e1', '#fff3e0', '#fbe9e7', '#efebe9', '#eceff1',
  // Colores medios (para destacar)
  '#ffcdd2', '#f8bbd9', '#e1bee7', '#d1c4e9', '#c5cae9', '#bbdefb',
  '#b3e5fc', '#b2ebf2', '#b2dfdb', '#c8e6c9', '#dcedc8', '#f0f4c3'
];

const LessonCreator = ({ lessons, onChange }) => {
  const [newLesson, setNewLesson] = useState({ 
    title: '', 
    content: '',
    order_in_course: (lessons.length + 1)
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  // Configuración MUY completa del editor Quill
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        // Fila 1: Encabezados y fuentes
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        
        // Fila 2: Formato básico
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        
        // Fila 3: COLORES - Paleta extendida
        [{ 'color': TEXT_COLORS }],
        [{ 'background': BACKGROUND_COLORS }],
        
        // Fila 4: Alineación y dirección
        [{ 'align': [] }],
        [{ 'direction': 'rtl' }],
        
        // Fila 5: Listas e indentación
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        
        // Fila 6: Bloques especiales
        ['blockquote', 'code-block'],
        
        // Fila 7: Multimedia y links
        ['link', 'image', 'video'],
        
        // Fila 8: Limpiar
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'script',
    'color', 'background',
    'align', 'direction',
    'list', 'bullet', 'check', 'indent',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  const handleAddLesson = () => {
    if (newLesson.title && newLesson.content) {
      const lessonToAdd = {
        ...newLesson,
        order_in_course: lessons.length + 1
      };
      onChange([...lessons, lessonToAdd]);
      setNewLesson({ 
        title: '', 
        content: '',
        order_in_course: lessons.length + 2
      });
      setShowEditor(false);
    }
  };

  const handleEditLesson = (index) => {
    setNewLesson({
      ...lessons[index],
      order_in_course: lessons[index].order_in_course || (index + 1)
    });
    setEditingIndex(index);
    setShowEditor(true);
  };

  const handleUpdateLesson = () => {
    const updatedLessons = [...lessons];
    updatedLessons[editingIndex] = {
      ...newLesson,
      order_in_course: newLesson.order_in_course || (editingIndex + 1)
    };
    onChange(updatedLessons);
    setNewLesson({ 
      title: '', 
      content: '',
      order_in_course: lessons.length + 1
    });
    setEditingIndex(null);
    setShowEditor(false);
  };

  const handleRemoveLesson = (index) => {
    const updatedLessons = lessons
      .filter((_, i) => i !== index)
      .map((lesson, newIndex) => ({
        ...lesson,
        order_in_course: newIndex + 1
      }));
    onChange(updatedLessons);
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewLesson({ title: '', content: '', order_in_course: updatedLessons.length + 1 });
      setShowEditor(false);
    }
  };

  const handleCancelEdit = () => {
    setNewLesson({ title: '', content: '', order_in_course: lessons.length + 1 });
    setEditingIndex(null);
    setShowEditor(false);
  };

  const toggleExpand = (index) => {
    setExpandedLesson(expandedLesson === index ? null : index);
  };

  return (
    <Box>
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MenuBookIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
              Lecciones del Curso
            </Typography>
            <Typography variant="body2" sx={{ color: '#636e72' }}>
              {lessons.length} {lessons.length === 1 ? 'lección creada' : 'lecciones creadas'}
            </Typography>
          </Box>
        </Box>
        
        {!showEditor && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowEditor(true)}
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              boxShadow: `0 4px 14px ${alpha(COLORS.gradient1, 0.4)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(COLORS.gradient1, 0.5)}`
              }
            }}
          >
            Nueva Lección
          </Button>
        )}
      </Box>

      {/* Lista de lecciones existentes */}
      {lessons.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {lessons.map((lesson, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: 2,
                border: '2px solid #e0e0e0',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(COLORS.primary, 0.3),
                  boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.1)}`
                }
              }}
            >
              {/* Header de la lección */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: expandedLesson === index ? alpha(COLORS.primary, 0.05) : 'white'
                }}
              >
                <DragIndicatorIcon sx={{ color: '#bdbdbd', mr: 1, cursor: 'grab' }} />
                
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: alpha(COLORS.primary, 0.15),
                    color: COLORS.primary,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    mr: 2
                  }}
                >
                  {index + 1}
                </Avatar>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    sx={{ 
                      fontWeight: 600, 
                      color: COLORS.dark,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {lesson.title}
                  </Typography>
                </Box>

                <Chip
                  icon={<CheckCircleIcon />}
                  label="Completada"
                  size="small"
                  sx={{
                    bgcolor: alpha(COLORS.success, 0.15),
                    color: COLORS.success,
                    fontWeight: 600,
                    mr: 1,
                    '& .MuiChip-icon': { color: COLORS.success }
                  }}
                />

                <Tooltip title="Ver contenido">
                  <IconButton 
                    size="small" 
                    onClick={() => toggleExpand(index)}
                    sx={{ color: COLORS.primary }}
                  >
                    {expandedLesson === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Editar">
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditLesson(index)}
                    sx={{ color: COLORS.warning }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Eliminar">
                  <IconButton 
                    size="small" 
                    onClick={() => handleRemoveLesson(index)}
                    sx={{ color: COLORS.error }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Contenido expandido */}
              <Collapse in={expandedLesson === index}>
                <Divider />
                <Box 
                  sx={{ 
                    p: 3, 
                    bgcolor: '#f8fffe',
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2 },
                    '& p': { mb: 2, lineHeight: 1.7 },
                    '& h1, & h2, & h3': { mt: 2, mb: 1, color: COLORS.dark },
                    '& ul, & ol': { pl: 3 },
                    '& blockquote': { 
                      borderLeft: `4px solid ${COLORS.primary}`,
                      pl: 2,
                      ml: 0,
                      color: '#636e72',
                      fontStyle: 'italic'
                    },
                    '& pre': {
                      bgcolor: '#2d3436',
                      color: '#dfe6e9',
                      p: 2,
                      borderRadius: 2,
                      overflow: 'auto'
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: lesson.content }}
                />
              </Collapse>
            </Paper>
          ))}
        </Box>
      )}

      {/* Editor de nueva lección / edición */}
      <Collapse in={showEditor}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `2px solid ${editingIndex !== null ? COLORS.warning : COLORS.primary}`,
            bgcolor: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(editingIndex !== null ? COLORS.warning : COLORS.primary, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {editingIndex !== null ? (
                <EditIcon sx={{ color: COLORS.warning }} />
              ) : (
                <AddIcon sx={{ color: COLORS.primary }} />
              )}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                {editingIndex !== null ? `Editando Lección ${editingIndex + 1}` : 'Nueva Lección'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#636e72', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormatColorTextIcon sx={{ fontSize: 16 }} />
                Editor con colores, fondos, imágenes y más
              </Typography>
            </Box>
          </Box>

          {/* Título de la lección */}
          <TextField
            fullWidth
            label="Título de la lección"
            placeholder="Ej: Introducción a la seguridad alimentaria"
            value={newLesson.title}
            onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': { borderColor: COLORS.primary },
                '&.Mui-focused fieldset': { borderColor: COLORS.primary }
              }
            }}
          />

          {/* Guía rápida de formato */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: alpha(COLORS.info, 0.08),
              border: `1px solid ${alpha(COLORS.info, 0.2)}`
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.primary, mb: 1 }}>
              💡 Guía rápida del editor
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" sx={{ color: '#636e72' }}>
                  <strong>🎨 Color texto:</strong> Selecciona texto → Click en "A" con color
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" sx={{ color: '#636e72' }}>
                  <strong>🖌️ Fondo:</strong> Selecciona texto → Click en icono de resaltado
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" sx={{ color: '#636e72' }}>
                  <strong>📷 Imagen:</strong> Click en icono de imagen → Pegar URL
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" sx={{ color: '#636e72' }}>
                  <strong>🎬 Video:</strong> Click en icono de video → Pegar URL YouTube
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Editor de contenido con estilos mejorados */}
          <Box
            sx={{
              '& .quill': {
                borderRadius: 2,
                border: '2px solid #e0e0e0',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: alpha(COLORS.primary, 0.5)
                },
                '&:focus-within': {
                  borderColor: COLORS.primary
                }
              },
              '& .ql-toolbar': {
                borderBottom: '2px solid #e0e0e0',
                bgcolor: '#f8f9fa',
                padding: '12px',
                '& .ql-formats': {
                  marginRight: '10px'
                },
                '& .ql-picker-label': {
                  color: COLORS.dark,
                  fontWeight: 500
                },
                '& button': {
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: alpha(COLORS.primary, 0.1),
                    color: COLORS.primary
                  }
                },
                '& .ql-active': {
                  backgroundColor: `${alpha(COLORS.primary, 0.15)} !important`,
                  color: `${COLORS.primary} !important`
                },
                // Estilo para el picker de color
                '& .ql-color-picker, & .ql-background': {
                  width: '32px'
                },
                '& .ql-picker-options': {
                  padding: '8px',
                  width: '200px'
                }
              },
              '& .ql-container': {
                minHeight: 350,
                fontSize: '1rem',
                fontFamily: 'inherit'
              },
              '& .ql-editor': {
                minHeight: 350,
                padding: '20px',
                lineHeight: 1.8,
                '&.ql-blank::before': {
                  color: '#9e9e9e',
                  fontStyle: 'normal',
                  left: '20px'
                },
                // Estilos para el contenido
                '& h1': { fontSize: '2em', fontWeight: 700, color: COLORS.dark },
                '& h2': { fontSize: '1.5em', fontWeight: 700, color: COLORS.dark },
                '& h3': { fontSize: '1.17em', fontWeight: 700, color: COLORS.dark },
                '& blockquote': {
                  borderLeft: `4px solid ${COLORS.primary}`,
                  paddingLeft: '16px',
                  margin: '16px 0',
                  color: '#636e72'
                },
                '& pre': {
                  backgroundColor: '#2d3436',
                  color: '#dfe6e9',
                  padding: '16px',
                  borderRadius: '8px'
                }
              }
            }}
          >
            <ReactQuill
              theme="snow"
              value={newLesson.content}
              onChange={(content) => setNewLesson({ ...newLesson, content })}
              modules={modules}
              formats={formats}
              placeholder="Escribe el contenido de la lección aquí...

Puedes usar:
• Títulos (H1-H6) para estructurar el contenido
• Colores de texto y fondos para destacar información importante
• Listas numeradas y con viñetas
• Imágenes y videos de YouTube
• Bloques de código y citas"
            />
          </Box>

          {/* Botones de acción */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancelEdit}
              sx={{
                borderColor: '#9e9e9e',
                color: '#636e72',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#636e72',
                  bgcolor: alpha('#636e72', 0.08)
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={editingIndex !== null ? <SaveIcon /> : <AddIcon />}
              onClick={editingIndex !== null ? handleUpdateLesson : handleAddLesson}
              disabled={!newLesson.title || !newLesson.content}
              sx={{
                background: editingIndex !== null 
                  ? `linear-gradient(135deg, ${COLORS.warning} 0%, ${COLORS.orange} 100%)`
                  : `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                '&.Mui-disabled': {
                  background: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              {editingIndex !== null ? 'Guardar Cambios' : 'Añadir Lección'}
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Mensaje si no hay lecciones */}
      {lessons.length === 0 && !showEditor && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed #e0e0e0',
            bgcolor: alpha(COLORS.primary, 0.02)
          }}
        >
          <MenuBookIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#636e72', mb: 1 }}>
            No hay lecciones todavía
          </Typography>
          <Typography variant="body2" sx={{ color: '#9e9e9e', mb: 2 }}>
            Haz clic en "Nueva Lección" para comenzar a crear el contenido del curso
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowEditor(true)}
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            Crear primera lección
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default LessonCreator;